<template>
  <div class="content container">
    <h1 class="heading">Installations</h1>

    <router-link :to="{ name: `addInstallation` }" class="btn btn--main btn--action">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
        <g transform="translate(28 278)">
          <path
            d="M4-222.1a23.9 23.9 0 1 1 0-47.8 23.9 23.9 0 0 1 0 47.8zm0-45.2c-11.7 0-21.3 9.6-21.3 21.3s9.6 21.3 21.3 21.3 21.3-9.6 21.3-21.3-9.6-21.3-21.3-21.3z"
            class="fill-current"
          />
          <path d="M-8.7-247.4h25.4v2.8H-8.7z" class="fill-current" />
          <path d="M2.6-258.7h2.8v25.4H2.6z" class="fill-current" />
        </g>
      </svg>
      Add a new installation</router-link
    >

    <transition-group name="fade" mode="out-in">
      <div v-if="!loaded" key="loading" class="loading-panel">Loading...</div>
      <div key="loaded-no-installations" v-else-if="!installations.length" class="pb-6">
        No installations found. Add your first installation.
      </div>
      <div class="pb-6" v-else key="loaded-installations">
        <h2 class="subheading">Filter the installations</h2>
        <div class="flex flex-row filters">
          <div class="flex-1">
            <label>Name</label>
            <input
              type="search"
              placeholder="Installation name"
              v-model="filterKey"
              class="border border-grey-light px-4 py-2 leading-normal w-full"
            />
          </div>

          <div class="flex-1" style="max-width: 170px">
            <label>Local Authorities</label>

            <multiselect
              label="name"
              track-by="name"
              :show-labels="false"
              v-model="filterLocalAuthorities"
              :options="localAuthorities"
            >
              <template slot="singleLabel" slot-scope="props">{{ props.option.name }}</template>
              <template slot="option" slot-scope="props">
                <span class="name">{{ props.option.name }}</span>
                <span class="count">{{ props.option.count }}</span>
              </template>
            </multiselect>
          </div>

          <div class="flex-1" style="max-width: 170px">
            <label>Type</label>

            <multiselect
              track-by="name"
              :show-labels="false"
              v-model="filterTypes"
              :options="types"
            >
              <template slot="singleLabel" slot-scope="props">{{ props.option.name }}</template>
              <template slot="option" slot-scope="props">
                <span class="name">{{ props.option.name }}</span>
                <span class="count">{{ props.option.count }}</span>
              </template>
            </multiselect>
          </div>

          <div class="flex-1" style="max-width: 170px">
            <label>Energy type</label>

            <multiselect
              track-by="name"
              :show-labels="false"
              v-model="filterEnergyTypes"
              :options="energyTypes"
            >
              <template slot="singleLabel" slot-scope="props">{{ props.option.name }}</template>
              <template slot="option" slot-scope="props">
                <span class="name">{{ props.option.name }}</span>
                <span class="count">{{ props.option.count }}</span>
              </template>
            </multiselect>
          </div>
        </div>

        <hr />

        <table class="styled-table">
          <thead>
            <tr>
              <th>id</th>
              <th>name</th>
              <th></th>
            </tr>
          </thead>
          <transition-group name="flip-list" tag="tbody">
            <tr
              v-for="installation in filteredInstallations"
              :key="installation._id"
              class="relative"
            >
              <td>
                <span
                  class="inline-block mr-2 rounded-full h-2 w-2 align-middle"
                  :class="{ 'bg-red': !installation.public, 'bg-green': installation.public }"
                ></span>
                {{ installation._id }}
              </td>
              <td>{{ installation.name }}</td>
              <td class="text-right">
                <router-link
                  :to="{ name: 'editInstallation', params: { id: installation._id } }"
                  class="btn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path
                      fill-rule="evenodd"
                      d="M12.3 3.7L0 16v4h4L16.3 7.7l-4-4zm1.4-1.4L16 0l4 4-2.3 2.3-4-4z"
                    ></path>
                  </svg>
                  Edit
                </router-link>

                <a
                  :href="`https://peoplespowerstation.org/installation/${installation.name}`"
                  class="btn"
                  target="_blank"
                >
                  View
                  <svg
                    height="48"
                    viewBox="0 0 48 48"
                    width="48"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M38 38H10V10h14V6H10c-2.21 0-4 1.79-4 4v28c0 2.21 1.79 4 4 4h28c2.21 0 4-1.79 4-4V24h-4v14zM28 6v4h7.17L15.51 29.66l2.83 2.83L38 12.83V20h4V6H28z"
                    ></path>
                  </svg>
                </a>

                <button class="delete-button" @click.prevent="mustConfirmDelete = installation._id">
                  delete
                </button>

                <transition name="fade">
                  <div
                    class="must-confirm-delete"
                    v-if="mustConfirmDelete === installation._id"
                    :class="{ 'must-confirm-delete--active': deletingId === installation._id }"
                  >
                    Are you sure you want to
                    <strong>delete "{{ installation.name }}"</strong>?<br />
                    <div class="text-center">
                      <button
                        class="btn btn--warn mr-2"
                        @click.prevent="deleteInstallation(installation._id)"
                      >
                        Yes, delete.
                      </button>
                      <button class="btn" @click.prevent="mustConfirmDelete = false">
                        No, do not delete.
                      </button>
                    </div>
                  </div>
                </transition>
              </td>
            </tr>
          </transition-group>
        </table>
      </div>
    </transition-group>
  </div>
</template>

<script>
import axios from '@/axios';

export default {
  name: 'dashboard',
  data() {
    return {
      deletingId: false,
      mustConfirmDelete: false,
      loaded: false,
      installations: [],
      error: null,
      filterLocalAuthorities: { name: 'All' },
      filterTypes: { name: 'All' },
      filterEnergyTypes: { name: 'All' },
      filterKey: null
    };
  },
  created() {
    // fetch the data when the view is created and the data is
    // already being observed
    this.fetchData();
  },
  watch: {
    // call again the method if the route changes
    $route: 'fetchData'
  },
  computed: {
    filteredInstallations() {
      let data = this.installations;
      let filterLocalAuthorities = this.filterLocalAuthorities
        ? this.filterLocalAuthorities.name
        : 'All';
      let filterTypes = this.filterTypes ? this.filterTypes.name : 'All';
      let filterEnergyTypes = this.filterEnergyTypes ? this.filterEnergyTypes.name : 'All';

      if (this.filterKey) {
        // Filter by text
        const filterKey = this.filterKey.toLowerCase();
        data = this.installations.filter(row => {
          return Object.keys(row).some(key => {
            return (
              String(row[key])
                .toLowerCase()
                .indexOf(filterKey) > -1
            );
          });
        });
      }

      return data.filter(installation => {
        let show = true;

        if (
          filterLocalAuthorities !== 'All' &&
          installation.localAuthority !== filterLocalAuthorities
        ) {
          // Filter by LAs
          show = false;
        }

        if (filterTypes !== 'All' && installation.type !== filterTypes) {
          // Filter by type
          show = false;
        }
        if (filterEnergyTypes !== 'All' && filterEnergyTypes !== installation.energyType) {
          // Filter by Energy Type
          show = false;
        }

        return show;
      });
    },
    energyTypes() {
      return this.uniqueProperties(this.installations, 'energyType');
    },
    types() {
      return this.uniqueProperties(this.installations, 'type');
    },
    localAuthorities() {
      return this.uniqueProperties(this.installations, 'localAuthority');
    }
  },
  methods: {
    errorDeleting() {
      this.$snotify.error('There was a problem deleting the installation.', {
        pauseOnHover: true,
        showProgressBar: true,
        timeout: 3000
      });

      this.deletingId = false;
    },
    successDeleting(id) {
      this.$snotify.success('Deleted successfully.', {
        pauseOnHover: true,
        showProgressBar: true,
        timeout: 3000
      });

      this.installations = this.installations.filter(installation => installation._id !== id);

      this.deletingId = false;
      this.mustConfirmDelete = false;
    },
    async deleteInstallation(id) {
      // mark as being deleted
      this.deletingId = id;

      const API_call = await axios.delete(`/api/v2/installations/${id}`).catch(this.errorDeleting);

      if (!API_call) {
        return;
      }

      this.successDeleting(id);
    },
    // https://router.vuejs.org/guide/advanced/data-fetching.html#fetching-before-navigation
    async fetchData() {
      this.installations = [];
      this.loading = true;

      const API_call = await axios.get('/api/v2/installations/full').catch(() => false);

      if (!API_call) {
        // TODO: show error
        return;
      }

      this.loaded = true;
      this.installations = API_call.data;
    },
    uniqueProperties(list, propertyName) {
      let total = 0;

      const allProps = list.reduce((props, item) => {
        const prop = item[propertyName];

        if (prop && !props[prop]) {
          props[prop] = 0;
        }

        if (prop) {
          props[prop]++;
        }
        total++;

        return props;
      }, {});

      let arrayMode = [];

      Object.keys(allProps).forEach(key => {
        arrayMode.push({
          name: key,
          count: allProps[key]
        });
      });

      const orderedArray = arrayMode.sort((a, b) => {
        if (a.name >= b.name) {
          return 1;
        }

        if (a.name <= b.name) {
          return -1;
        }

        return 0;
      });

      return [
        {
          name: 'All',
          count: total
        },
        ...orderedArray
      ];
    }
  }
};
</script>

<style lang="sass">
.loading-panel + div
  @apply -mt-6

.must-confirm-delete
  background: #fff
  border-left: solid 1px #efefef
  border-right: solid 1px #efefef
  bottom: 1px
  padding: 0 10px
  position: absolute
  right: 0
  text-align: right
  top: 0

  &:after
    align-items: center
    background: rgba(#fff, .9)
    bottom: 0
    content: 'Deleting...'
    font-weight: 700
    display: flex
    left: 0
    justify-content: center
    opacity: 0
    position: absolute
    right: 0
    top: 0
    visibility: hidden
    transition: all .3s ease-in

  &--active:after
    opacity: 1
    visibility: visible
    transition: all .3s ease-out

.delete-button
  @apply text-red-dark
  background: none
  border: 0
  font-size: 13px
  margin: 0 0 0 10px
  opacity: 0
  text-decoration: underline
  visibility: hidden

  &:hover,
  &:focus
    text-decoration: none

tr.relative:hover .delete-button
  opacity: 1
  visibility: visible
</style>
